
// frontend/src/components/Sports/ScoreboardGrid.js
// REDESIGNED: Premium Glass Score Cards with Game Info & Team Links
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSportsScores } from '../../services/SportsDataService';
import { PlayCircle, Radio, Clock, ExternalLink, Share2, X, Check, Copy, MapPin, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

// League logo URLs (ESPN CDN)
const LEAGUE_LOGOS = {
    'eng.1': 'https://a.espncdn.com/i/leaguelogos/soccer/500/23.png',
    'esp.1': 'https://a.espncdn.com/i/leaguelogos/soccer/500/15.png',
    'ger.1': 'https://a.espncdn.com/i/leaguelogos/soccer/500/10.png',
    'ita.1': 'https://a.espncdn.com/i/leaguelogos/soccer/500/12.png',
    'uefa.champions': 'https://a.espncdn.com/i/leaguelogos/soccer/500/2.png',
    'nba': 'https://a.espncdn.com/i/teamlogos/leagues/500/nba.png',
    'nfl': 'https://a.espncdn.com/i/teamlogos/leagues/500/nfl.png',
    'nhl': 'https://a.espncdn.com/i/teamlogos/leagues/500/nhl.png',
    'f1': 'https://a.espncdn.com/i/teamlogos/leagues/500/f1.png'
};

// League slug mapping for team URLs
const LEAGUE_SLUG_MAP = {
    'eng.1': { sport: 'soccer', league: 'premier-league' },
    'esp.1': { sport: 'soccer', league: 'la-liga' },
    'ger.1': { sport: 'soccer', league: 'bundesliga' },
    'ita.1': { sport: 'soccer', league: 'serie-a' },
    'uefa.champions': { sport: 'soccer', league: 'champions-league' },
    'nba': { sport: 'basketball', league: 'nba' },
    'nfl': { sport: 'football', league: 'nfl' },
    'nhl': { sport: 'hockey', league: 'nhl' },
};

// Convert team name to SEO-friendly slug
const teamNameToSlug = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .replace(/['']/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/(^-|-$)/g, '');
};

// Helper: Format countdown or match time
const getMatchTimeDisplay = (event) => {
    const matchDate = new Date(event.date);
    const now = new Date();
    const diff = matchDate - now;

    if (diff <= 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

// Format date for display
const formatMatchDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

// X (Twitter) Icon
const XIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

// Facebook Icon
const FacebookIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
);

// WhatsApp Icon
const WhatsAppIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

// Telegram Icon
const TelegramIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
);

// Elite Share Overlay Component
const ShareOverlay = ({ event, home, away, onClose }) => {
    const [copied, setCopied] = useState(false);

    const status = event.competitions[0].status.type;
    const isLive = status.state === 'in';
    const isCompleted = status.state === 'post';

    // Build engaging share message
    const matchup = `${home.team.abbreviation} vs ${away.team.abbreviation}`;
    const scoreInfo = isLive || isCompleted
        ? `${home.team.abbreviation} ${home.score} - ${away.score} ${away.team.abbreviation}`
        : matchup;

    const statusEmoji = isLive ? '🔴 LIVE NOW' : isCompleted ? '✅ FINAL' : '📅 Upcoming';

    // Premium share message
    const shareText = `${statusEmoji}: ${scoreInfo}\n\n🏆 Check out this game on XtreamGate Sports Hub!\n\n`;
    const shareUrl = 'https://xtreamgate.com/sports';
    const fullMessage = shareText + shareUrl;

    const socialLinks = [
        {
            name: 'X',
            icon: XIcon,
            color: 'bg-black hover:bg-zinc-800 border border-white/20',
            getUrl: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
        },
        {
            name: 'Facebook',
            icon: FacebookIcon,
            color: 'bg-[#1877F2] hover:bg-[#166FE5]',
            getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
        },
        {
            name: 'WhatsApp',
            icon: WhatsAppIcon,
            color: 'bg-[#25D366] hover:bg-[#20BD5C]',
            getUrl: () => `https://wa.me/?text=${encodeURIComponent(fullMessage)}`
        },
        {
            name: 'Telegram',
            icon: TelegramIcon,
            color: 'bg-[#0088CC] hover:bg-[#007AB8]',
            getUrl: () => `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
        }
    ];

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(fullMessage);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-20 flex flex-col"
            style={{
                background: 'rgba(10, 10, 10, 0.95)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-primary" />
                    <span className="text-sm font-bold text-white">Share This Match</span>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-muted-foreground hover:text-white transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Match Preview */}
            <div className="px-4 py-3 border-b border-white/5">
                <div className="text-xs text-muted-foreground mb-1">{statusEmoji}</div>
                <div className="text-sm font-bold text-white">{scoreInfo}</div>
            </div>

            {/* Social Buttons */}
            <div className="flex-1 p-4 space-y-3">
                <div className="grid grid-cols-4 gap-2">
                    {socialLinks.map((social) => (
                        <a
                            key={social.name}
                            href={social.getUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setTimeout(onClose, 100)}
                            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-all transform hover:scale-105 ${social.color}`}
                        >
                            <social.icon />
                            <span className="text-[10px] font-medium text-white/80">{social.name}</span>
                        </a>
                    ))}
                </div>

                {/* Copy Link Button */}
                <button
                    onClick={copyLink}
                    className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-all ${copied
                        ? 'bg-green-500/20 border border-green-500/50'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                        }`}
                >
                    {copied ? (
                        <>
                            <Check className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-bold text-green-400">Copied to Clipboard!</span>
                        </>
                    ) : (
                        <>
                            <Copy className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Copy Message</span>
                        </>
                    )}
                </button>
            </div>
        </motion.div>
    );
};

// Game Info Panel Component
const GameInfoPanel = ({ event, competition }) => {
    const venue = competition.venue;

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10"
        >
            <div className="px-4 py-3 space-y-2 bg-white/5">
                {/* Date & Time */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>{formatMatchDate(event.date)}</span>
                </div>

                {/* Venue */}
                {venue && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span>{venue.fullName || venue.shortName || 'TBD'}</span>
                    </div>
                )}

                {/* League/Competition */}
                {event.season && (
                    <div className="text-xs text-muted-foreground">
                        {event.season.displayName || event.season.year}
                    </div>
                )}
            </div>
        </motion.div>
    );
};

// Clickable Team Logo Component
const TeamLogo = ({ team, leagueId, isLive }) => {
    const slugInfo = LEAGUE_SLUG_MAP[leagueId];
    const teamSlug = teamNameToSlug(team.shortDisplayName || team.displayName || team.abbreviation);

    // If we have league info, make it a link
    if (slugInfo && teamSlug) {
        return (
            <Link
                to={`/sports/${slugInfo.sport}/${slugInfo.league}/team/${teamSlug}`}
                className="block group"
                onClick={(e) => e.stopPropagation()} // Prevent card click
            >
                <div className={`relative ${isLive ? 'animate-pulse' : ''} group-hover:scale-110 transition-transform`}>
                    <img
                        src={team.logo}
                        alt={team.displayName}
                        className="w-14 h-14 object-contain"
                    />
                    {isLive && (
                        <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
                    )}
                </div>
            </Link>
        );
    }

    // Fallback: non-clickable logo
    return (
        <div className={`relative ${isLive ? 'animate-pulse' : ''}`}>
            <img
                src={team.logo}
                alt={team.displayName}
                className="w-14 h-14 object-contain"
            />
            {isLive && (
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
            )}
        </div>
    );
};

const ScoreCard = ({ event, index, leagueId }) => {
    const [showShare, setShowShare] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const competition = event.competitions[0];
    const status = competition.status.type;
    const isLive = status.state === 'in';
    const isCompleted = status.state === 'post';
    const isPre = status.state === 'pre';

    const home = competition.competitors.find(c => c.homeAway === 'home');
    const away = competition.competitors.find(c => c.homeAway === 'away');

    // Toggle game info panel when clicking the card body
    const handleCardClick = (e) => {
        // Don't toggle if clicking on share overlay or buttons
        if (e.target.closest('a') || e.target.closest('button')) return;
        setShowInfo(!showInfo);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ y: -4, scale: 1.02 }}
            className={`
                relative overflow-hidden rounded-2xl border transition-all duration-300 cursor-pointer
                ${isLive
                    ? 'border-red-500/50 shadow-[0_0_30px_rgba(239,68,68,0.2)]'
                    : 'border-white/10 hover:border-primary/30'
                }
            `}
            style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(12px)',
            }}
            onClick={handleCardClick}
        >
            {/* Share Overlay */}
            <AnimatePresence>
                {showShare && (
                    <ShareOverlay
                        event={event}
                        home={home}
                        away={away}
                        onClose={() => setShowShare(false)}
                    />
                )}
            </AnimatePresence>

            {/* Live Pulse Indicator */}
            {isLive && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-500 animate-pulse" />
            )}

            {/* Header / Status */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 relative z-10">
                <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${isLive ? 'text-red-400' : 'text-muted-foreground'}`}>
                    {isLive && <Radio className="w-3 h-3 animate-pulse" />}
                    {isPre && <Clock className="w-3 h-3" />}
                    <span>{status.shortDetail}</span>
                </div>
                <div className="flex items-center gap-3">
                    {isLive && (
                        <div className="flex items-center gap-1 text-xs font-bold text-primary">
                            <PlayCircle className="w-3 h-3" />
                            <span>LIVE</span>
                        </div>
                    )}
                    {/* Expand/Collapse Indicator */}
                    <div className="text-muted-foreground">
                        {showInfo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                    {/* Share Button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowShare(true); }}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-primary/20 text-muted-foreground hover:text-primary transition-all"
                        title="Share Match"
                    >
                        <Share2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Teams & Scores */}
            <div className="p-5">
                <div className="grid grid-cols-3 items-center gap-4">
                    {/* Home Team */}
                    <div className="flex flex-col items-center text-center gap-3">
                        <TeamLogo team={home.team} leagueId={leagueId} isLive={isLive} />
                        <span className="text-sm font-bold text-white leading-tight">
                            {home.team.abbreviation || home.team.shortDisplayName}
                        </span>
                    </div>

                    {/* Score Board */}
                    <div className="flex flex-col items-center justify-center">
                        {isPre ? (
                            <div className="flex flex-col items-center">
                                <div className="text-2xl font-mono text-muted-foreground tracking-widest">VS</div>
                                {getMatchTimeDisplay(event) && (
                                    <div className="mt-1 px-2 py-1 bg-primary/10 rounded-full">
                                        <span className="text-xs font-bold text-primary">
                                            ⏱ {getMatchTimeDisplay(event)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <span className={`text-3xl font-black font-mono ${parseInt(home.score) > parseInt(away.score) ? 'text-primary' : 'text-white'}`}>
                                    {home.score}
                                </span>
                                <span className="text-lg text-muted-foreground">-</span>
                                <span className={`text-3xl font-black font-mono ${parseInt(away.score) > parseInt(home.score) ? 'text-primary' : 'text-white'}`}>
                                    {away.score}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Away Team */}
                    <div className="flex flex-col items-center text-center gap-3">
                        <TeamLogo team={away.team} leagueId={leagueId} isLive={isLive} />
                        <span className="text-sm font-bold text-white leading-tight">
                            {away.team.abbreviation || away.team.shortDisplayName}
                        </span>
                    </div>
                </div>
            </div>

            {/* Game Info Panel (Expandable) */}
            <AnimatePresence>
                {showInfo && (
                    <GameInfoPanel event={event} competition={competition} />
                )}
            </AnimatePresence>

            {/* Footer CTA */}
            <div className="px-4 py-3 bg-white/5 border-t border-white/5 flex items-center justify-between gap-2">
                <a
                    href="https://xtreamcube.shop/freetrialiptv/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-primary/90 transition-all"
                >
                    <PlayCircle className="w-3 h-3" />
                    Watch Now
                </a>
                <Link
                    to="/blog/best-iptv-for-sports-2026"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                    <span>Guide</span>
                    <ExternalLink className="w-3 h-3" />
                </Link>
            </div>
        </motion.div>
    );
};

export const ScoreboardGrid = ({ sport, league, limit }) => {
    const { games, leagueName, isLoading, isError } = useSportsScores(sport, league);

    const displayGames = limit ? games.slice(0, limit) : games;

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(limit || 4)].map((_, i) => (
                    <div key={i} className="h-48 rounded-2xl bg-white/5 animate-pulse" />
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-8 text-center rounded-2xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 font-medium">Failed to load scores. Please try again.</p>
            </div>
        );
    }

    if (displayGames.length === 0) {
        return (
            <div className="p-8 text-center rounded-2xl bg-white/5 border border-white/10">
                <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No matches scheduled today.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                {LEAGUE_LOGOS[league] && (
                    <img
                        src={LEAGUE_LOGOS[league]}
                        alt={leagueName}
                        className="w-10 h-10 object-contain"
                    />
                )}
                <div>
                    <h3 className="text-xl font-bold text-white">Live {leagueName} Scores</h3>
                    <p className="text-xs text-muted-foreground">Updated every 30 seconds • Click card for details</p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayGames.map((game, index) => (
                    <ScoreCard key={game.id} event={game} index={index} leagueId={league} />
                ))}
            </div>
        </div>
    );
};
